import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Table,
  Badge,
  Group,
  Select,
  Loader,
  Text,
  Stack,
  Paper,
  Button,
  Modal,
  Pagination,
  ActionIcon,
  Tooltip,
  Center,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconFilter, IconRefresh, IconSearch } from "@tabler/icons-react";
import axios from "axios";
import dayjs from "dayjs";
import {
  OBSERVATION_SEVERITY_OPTIONS as SEVERITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS as STATUS_OPTIONS,
  observationSeverityColor,
  observationStatusColor,
  observationStatusLabel,
  type ObservationSeverity,
  type ObservationStatus,
} from "../../utils/observationStatus";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const ITEMS_PER_PAGE = 20;

interface ObservationSummary {
  id: string;
  status: ObservationStatus;
  severity: ObservationSeverity | null;
  createdAt: string;
  displayCode: string | null;
}

interface ObservationListResponse {
  observations: ObservationSummary[];
  total: number;
}

interface Filters {
  status?: string;
  severity?: string;
  fromDate?: string | null;
  toDate?: string | null;
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function MyObservationsListPage() {
  const navigate = useNavigate();

  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<Filters>({});
  const [pendingFilters, setPendingFilters] = useState<Filters>({});
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useQuery<ObservationListResponse>(
    {
      queryKey: [
        "my-observations",
        filters.status,
        filters.severity,
        filters.fromDate,
        filters.toDate,
        sortOrder,
      ],
      queryFn: async () => {
        const params: Record<string, string> = { sortOrder };
        if (filters.status) params.status = filters.status;
        if (filters.severity) params.severity = filters.severity;
        if (filters.fromDate)
          params.fromDate = dayjs(filters.fromDate)
            .startOf("day")
            .toISOString();
        if (filters.toDate)
          params.toDate = dayjs(filters.toDate).endOf("day").toISOString();
        const res = await axios.get<{
          success: boolean;
          data: ObservationListResponse;
        }>(`${API_BASE_URL}/api/my-observations`, {
          params,
          headers: getAuthHeaders(),
        });
        return res.data.data;
      },
    }
  );

  const observations = useMemo(() => data?.observations ?? [], [data]);

  const filteredData = useMemo(() => {
    if (!debouncedSearch) return observations;
    const q = debouncedSearch.toLowerCase();
    return observations.filter((o) =>
      [o.displayCode, o.status, o.severity]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q))
    );
  }, [observations, debouncedSearch]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const hasActiveFilters = useMemo(
    () =>
      !!(
        filters.status ||
        filters.severity ||
        filters.fromDate ||
        filters.toDate
      ),
    [filters]
  );

  const handleOpenFilterModal = useCallback(() => {
    setPendingFilters({ ...filters });
    openFilterModal();
  }, [filters, openFilterModal]);

  const handleApplyFilters = useCallback(() => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  }, [pendingFilters, closeFilterModal]);

  const handleClearPendingFilters = useCallback(() => {
    setPendingFilters({});
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  }, []);

  const handleRowClick = useCallback(
    (id: string) => navigate(`/my/observations/${id}`),
    [navigate]
  );

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>My Observations</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Track the observations you&apos;ve shared and follow their
                review status.
              </Text>
            </div>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by code (e.g. OBS-1), status, or severity..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Tooltip label="Refresh">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => refetch()}
                      loading={isLoading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status && (
                      <Badge variant="light" color="blue">
                        {observationStatusLabel(filters.status)}
                      </Badge>
                    )}
                    {filters.severity && (
                      <Badge
                        variant="light"
                        color={observationSeverityColor(filters.severity)}
                      >
                        {filters.severity}
                      </Badge>
                    )}
                    {(filters.fromDate || filters.toDate) && (
                      <Badge variant="light" color="violet">
                        {filters.fromDate
                          ? dayjs(filters.fromDate).format("MMM D")
                          : "…"}
                        {" – "}
                        {filters.toDate
                          ? dayjs(filters.toDate).format("MMM D")
                          : "…"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} observation
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {error && (
            <Text c="red">
              Failed to load observations:{" "}
              {error instanceof Error ? error.message : "Unknown error"}
            </Text>
          )}

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Observations ({filteredData.length})
                </Text>
                <Group gap="md">
                  <Select
                    value={sortOrder}
                    onChange={(val) => setSortOrder(val ?? "desc")}
                    data={[
                      { value: "desc", label: "Newest first" },
                      { value: "asc", label: "Oldest first" },
                    ]}
                    w={160}
                    size="sm"
                  />
                  {totalPages > 1 && (
                    <>
                      <Text size="sm" c="dimmed">
                        Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                        {Math.min(page * ITEMS_PER_PAGE, filteredData.length)}{" "}
                        of {filteredData.length}
                      </Text>
                      <Pagination
                        total={totalPages}
                        value={page}
                        onChange={setPage}
                        size="sm"
                      />
                    </>
                  )}
                </Group>
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || debouncedSearch
                    ? "No observations match the current search or filters"
                    : "You have not shared any observations yet."}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 100 }}>#</Table.Th>
                      <Table.Th style={{ width: 160 }}>Status</Table.Th>
                      <Table.Th style={{ width: 120 }}>Severity</Table.Th>
                      <Table.Th>Submitted</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((obs) => (
                      <Table.Tr
                        key={obs.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(obs.id)}
                      >
                        <Table.Td>
                          {obs.displayCode ? (
                            <Text size="sm" fw={600} ff="monospace">
                              {obs.displayCode}
                            </Text>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={observationStatusColor(obs.status)}
                            variant="light"
                            size="sm"
                          >
                            {observationStatusLabel(obs.status)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {obs.severity ? (
                            <Badge
                              color={observationSeverityColor(obs.severity)}
                              variant="light"
                              size="sm"
                            >
                              {obs.severity}
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {dayjs(obs.createdAt).format("MMM D, YYYY h:mm A")}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Observations"
          size="md"
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="Any status"
              data={STATUS_OPTIONS}
              value={pendingFilters.status ?? null}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: value ?? undefined,
                })
              }
              clearable
            />
            <Select
              label="Severity"
              placeholder="Any severity"
              data={SEVERITY_OPTIONS}
              value={pendingFilters.severity ?? null}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  severity: value ?? undefined,
                })
              }
              clearable
            />
            <DatePickerInput
              type="range"
              label="Date range"
              placeholder="Pick date range"
              value={[
                pendingFilters.fromDate ?? null,
                pendingFilters.toDate ?? null,
              ]}
              onChange={(range) =>
                setPendingFilters({
                  ...pendingFilters,
                  fromDate: range[0] ?? undefined,
                  toDate: range[1] ?? undefined,
                })
              }
              clearable
            />
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
